import { Box, Collapse, ListItemButton, ListItemText, SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const PREFIX = 'Collapsible';

const classes = {
  button: `${PREFIX}-button`,
  open: `${PREFIX}-open`,
  default: `${PREFIX}-default`,
};

const StyledCollapsible = styled('div', {
  label: 'Collapsible',
  shouldForwardProp: (props) => props !== 'disabled' && props !== 'hideBorder',
})<StyleProps>(({ theme, backgroundColor, disabled, hideBorder, marginBottom, marginTop }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(marginBottom),
  marginTop: theme.spacing(marginTop),
  background: backgroundColor,
  border: hideBorder ? 'none' : `1px solid ${disabled ? theme.palette.darkBlue.o50 : theme.palette.darkBlue.main}`,

  [`&.${classes.default}`]: {
    background: backgroundColor,
  },
  [`& .${classes.button}`]: {
    paddingLeft: '4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      opacity: 1,
    },
  },
  ['.MuiTableCell-body']: {
    backgroundColor: `${theme.palette.lightBlue.o25} !important`,
  },
  [`& .MuiCollapse-wrapper`]: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 0),
    },
  },
}));

const TitleBox = styled(Box, {
  label: 'TitleBox',
  shouldForwardProp: (props) => props !== 'hideHeaderBorder',
})<StyleProps>(({ theme, hideHeaderBorder }) => ({
  padding: theme.spacing(2, 4, 2),
  [`&.${classes.open}`]: {
    borderBottom: hideHeaderBorder ? 0 : `1px solid ${theme.palette.bluViu.o50}50`,
    borderBottomRightRadius: '0px',
    borderBottomLeftRadius: '0px',
  },
}));

const CollapseLabel = styled(Box, {
  shouldForwardProp: (props) => props !== 'disabled',
})<LabelProps>(({ theme, disabled }) => ({
  fontSize: 16,
  fontWeight: 500,
  color: disabled ? theme.palette.darkBlue.o50 : theme.palette.darkBlue.main,
  textTransform: 'none',
  paddingRight: 10,
  '& .MuiTypography-root': {
    color: 'inherit',
  },
}));

const IconBox = styled(Box, {
  shouldForwardProp: (props) => props !== 'disabled',
})<IconBoxProps>(({ theme, disabled }) => ({
  color: disabled ? theme.palette.darkBlue.o50 : theme.palette.primary.main,
  display: 'flex',
}));

type StyleProps = {
  padding: number;
  marginBottom: number;
  marginTop: number;
  backgroundColor: string;
  hideHeaderBorder?: boolean;
  hideBorder?: boolean;
  disabled?: boolean;
};

type LabelProps = {
  disabled: boolean;
};

type IconBoxProps = {
  disabled: boolean;
};

type Props = {
  children: JSX.Element;
  open?: boolean;
  count?: number;
  padding?: number;
  marginBottom?: number;
  marginTop?: number;
  id?: string;
  sx?: SxProps<Theme> | undefined;
  hideBorder?: boolean;
  hideHeaderBorder?: boolean;
  label?: string | React.ReactNode;
  onClick?: () => void;
  backgroundColor?: string;
  borderRadius?: number;
  disabled?: boolean;
};

const Collapsible: React.FC<Props> = ({
  children,
  label,
  open = false,
  count,
  padding = 4,
  sx,
  hideBorder = false,
  hideHeaderBorder = false,
  marginBottom = 2,
  marginTop = 0,
  borderRadius = 4,
  onClick,
  backgroundColor = '#fff',
  disabled = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const isExpanded = !disabled && isOpen;

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const target = e.target as HTMLElement;
    const container = e.currentTarget as HTMLElement;

    // Ignore clicks from portals (e.g., MUI Dialog)
    if (!container.contains(target)) {
      return;
    }

    // Ignore clicks on interactive elements (buttons, links, inputs, etc.)
    let el: HTMLElement | null = target;
    while (el && el !== container) {
      const isInteractive =
        ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) || el.getAttribute('role') === 'button';
      if (isInteractive) {
        return;
      }
      el = el.parentElement;
    }

    setIsOpen((prev) => !prev);
    onClick?.();
  };

  return (
    <StyledCollapsible
      sx={sx}
      className={isExpanded ? classes.open : classes.default}
      padding={padding}
      hideBorder={hideBorder}
      disabled={disabled}
      marginBottom={marginBottom}
      marginTop={marginTop}
      backgroundColor={backgroundColor}>
      <TitleBox
        hideHeaderBorder={hideHeaderBorder}
        padding={padding}
        marginBottom={0}
        marginTop={0}
        borderRadius={borderRadius}
        className={isExpanded ? classes.open : classes.default}
        backgroundColor={backgroundColor}>
        <ListItemButton
          disableRipple
          className={classes.button}
          disabled={disabled}
          onClick={handleOpen}
          sx={{
            padding: (theme) => theme.spacing(1, 0, 1),
          }}>
          <ListItemText
            primary={
              <CollapseLabel disabled={disabled}>{typeof label === 'string' && count ? `${label} (${count})` : label}</CollapseLabel>
            }
          />

          <IconBox disabled={disabled}>{isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}</IconBox>
        </ListItemButton>
      </TitleBox>

      <Collapse {...props} in={isExpanded}>
        {children}
      </Collapse>
    </StyledCollapsible>
  );
};

export default Collapsible;
